import { isElemScrolledToBottom, stopAllScrollObservers } from './utils';
import { sleep } from 'utils/async';
import { getMessageHtmlElem } from 'utils/message';
import {
  observerOptions,
  PX_TOLERANCE_IS_BOTTOM,
  RATIO_SHOW_CTA_LATEST_MSG,
  RATIO_TOLERANCE_IS_BOTTOM,
  TIME_BUFFER_ASYNC_SCROLL_UX,
  TIME_BUFFER_NEW_MSG_PARSING,
} from './config';
import { ACTIONS } from 'constants/hook';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { IObserverManualScrollProps } from './types';

/**
 * Create a dynamic Intersection Observer instance for when new message has already been received.
 * Handle when user is manually scrolling conversation thread.
 */
export const observerManualScroll = async ({
  dispatchViewAction,
  initMsgCountRef,
  initIntersectionRatioRef,
  isAdvocateInConvoAndLastMsgByUser,
  isAutoScrolling,
  mainContentRef,
  msgCount,
  msgLastObsManualScrollInstanceRef,
  unixTimeLastInit,
  unixTimeLastMsgReceived,
}: IObserverManualScrollProps) => {
  /** START scoped callback function **/
  const obsManualScrollCallback = async (obs: Array<IntersectionObserverEntry>) => {
    const lastMsgObs = obs[0];

    // See link below for why viewState needs to be accessed this way from a callback. We can leverage Global provider.
    // ref: https://stackoverflow.com/questions/57847594/react-hooks-accessing-up-to-date-state-from-within-a-callback
    const latestViewState = window[CHATUI_WINDOW_PATH].getViewState();
    const nextViewState = { ...latestViewState };

    /** General properties for cases below to identify when user is scrolled to the bottom of conversation thread. **/
    const isAtBottom = isElemScrolledToBottom(mainContentRef.current, PX_TOLERANCE_IS_BOTTOM);
    nextViewState.scrollToBottom.isAutoScrollNextMsg = isAtBottom;
    nextViewState.scrollToBottom.isLastMsgInView = lastMsgObs.intersectionRatio > 0;

    /**
     * Handle case where conversation has just initialized. Don't change any state values.
     * But prepare observer for future scrolling.
     */
    if (unixTimeLastInit && Date.now() - unixTimeLastInit < TIME_BUFFER_ASYNC_SCROLL_UX + 1500) {
      initIntersectionRatioRef.current = lastMsgObs.intersectionRatio;
      // console.log('ChatUI manual scroll scenario 0 - just loaded');
      return;
    }

    /** Do nothing when autoscroll scenario takes precedent for parsing. **/
    if (isAutoScrolling) {
      // console.log('ChatUI manual scroll scenario 1 - autoscrolled earlier.', lastMsgObs, 'isAtBottom:', isAtBottom);
      // When autoscroll took precedent, but message is really long.
      if (isAtBottom || lastMsgObs.intersectionRatio > RATIO_SHOW_CTA_LATEST_MSG) {
        // console.log('ChatUI manual scroll scenario 1 - autoscrolled handler, but reaching end');
        nextViewState.scrollToBottom.isCTAVisible = false;
        dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      }
      return;
    }

    /** Handle case where CTA has already been seen. **/
    if (latestViewState.scrollToBottom.hasSeenCTAThisMsg) {
      // console.log('ChatUI manual scroll scenario 2 - already seen CTA');
      if (lastMsgObs.intersectionRatio > 0) {
        nextViewState.scrollToBottom.isCTAVisible = false;
        dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      }
      return;
    }

    /** Handle case where initial scroll is at arbitrary part of conversation **/
    const isInitMsg = msgCount === initMsgCountRef.current;
    if (isInitMsg && !latestViewState.scrollToBottom.hasSeenCTAThisMsg) {
      // console.log('ChatUI manual scroll scenario 3 - initial message.');
      const isScrolledToBottomWithLargeBuffer = isElemScrolledToBottom(
        mainContentRef.current,
        3 * PX_TOLERANCE_IS_BOTTOM,
      );
      nextViewState.scrollToBottom.isCTAVisible = !isScrolledToBottomWithLargeBuffer;
      nextViewState.scrollToBottom.hasSeenCTAThisMsg = !isScrolledToBottomWithLargeBuffer;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      return;
    }

    /**
     * Handle case where user is connected to an advocate and adding additional
     * messages to the thread before advocate responds.
     *
     * Will auto-scroll to bottom via QuestionField component.
     */
    if (isAdvocateInConvoAndLastMsgByUser) {
      nextViewState.scrollToBottom.isCTAVisible = false;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      // console.log('ChatUI manual scroll scenario 5 - advocate');
      return;
    }

    /** Handle case where user is scrolling away from the last message **/
    if (lastMsgObs.intersectionRatio === 0) {
      // console.log('ChatUI manual scroll scenario 6 - scrolling away from lastMsg');
      const isMessageJustReceived =
        unixTimeLastMsgReceived &&
        Date.now() - unixTimeLastMsgReceived <= TIME_BUFFER_NEW_MSG_PARSING;
      nextViewState.scrollToBottom.isCTAVisible = isMessageJustReceived;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      return;
    }

    /** Handle case nearing bottom but CTA still visible. **/
    if (lastMsgObs.intersectionRatio > RATIO_TOLERANCE_IS_BOTTOM) {
      // console.log('ChatUI manual scroll scenario 7 - nearing bottom');
      nextViewState.scrollToBottom.isCTAVisible = false;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      return;
    }

    /** Handle case where user has long last message within view (observable). **/
    const isLongContent = lastMsgObs.intersectionRatio <= RATIO_SHOW_CTA_LATEST_MSG;
    if (lastMsgObs.intersectionRatio <= RATIO_SHOW_CTA_LATEST_MSG) {
      // console.log('ChatUI manual scroll scenario 9 - long content. IsAtBottom:', isAtBottom);
      const isCTAVisible = isAtBottom ? false : isLongContent;
      nextViewState.scrollToBottom.isCTAVisible = isCTAVisible;
      nextViewState.scrollToBottom.hasSeenCTAThisMsg = isCTAVisible;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
      return;
    }

    /** Handle recovery case where state value was previously not set properly. **/
    if (isAtBottom && latestViewState.scrollToBottom.isCTAVisible) {
      // console.log('ChatUI manual scroll scenario 10 - realignment. At bottom & CTA still visible');
      nextViewState.scrollToBottom.isCTAVisible = false;
      nextViewState.scrollToBottom.hasSeenCTAThisMsg = true;
      dispatchViewAction({ type: ACTIONS.CHANGE, payload: nextViewState });
    }

    // console.log('ChatUI manual scroll scenario 11 - else', lastMsgObs);
    return;
  };
  /** END scoped callback function **/

  await sleep(TIME_BUFFER_ASYNC_SCROLL_UX); // magic number. Be careful about changing.

  stopAllScrollObservers(msgLastObsManualScrollInstanceRef, msgCount);
  msgLastObsManualScrollInstanceRef.current = new IntersectionObserver(
    obsManualScrollCallback,
    observerOptions,
  );

  const elemMsgLastInConvo = getMessageHtmlElem.byIndex(msgCount - 1);
  elemMsgLastInConvo && msgLastObsManualScrollInstanceRef.current?.observe(elemMsgLastInConvo);
};
